import { useId } from 'react';

/**
 * FormField - Wrapper component for form fields with validation error display
 */
export default function FormField({ 
  label, 
  required = false, 
  error, 
  hint,
  children,
  className = '',
  id: providedId,
}) {
  const generatedId = useId();
  const fieldId = providedId || generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  
  return (
    <div className={className}>
      {label && (
        <label htmlFor={fieldId} className="label">
          {label}
          {required && <span aria-hidden="true"> *</span>}
          {required && <span className="sr-only"> (required)</span>}
        </label>
      )}
      
      {hint && !error && (
        <p 
          id={hintId}
          className="text-xs mb-1.5" 
          style={{ color: 'var(--color-text-muted)' }}
        >
          {hint}
        </p>
      )}
      
      {children}
      
      {error && (
        <p 
          id={errorId}
          className="text-xs mt-1 flex items-center gap-1"
          style={{ color: 'var(--color-status-error)' }}
          role="alert"
          aria-live="polite"
        >
          <ErrorIcon />
          {error}
        </p>
      )}
    </div>
  );
}

function ErrorIcon() {
  return (
    <svg 
      width="12" 
      height="12" 
      viewBox="0 0 12 12" 
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M6 0C2.69 0 0 2.69 0 6s2.69 6 6 6 6-2.69 6-6S9.31 0 6 0zm0 10.5c-.41 0-.75-.34-.75-.75s.34-.75.75-.75.75.34.75.75-.34.75-.75.75zm.75-3c0 .41-.34.75-.75.75s-.75-.34-.75-.75v-3c0-.41.34-.75.75-.75s.75.34.75.75v3z"/>
    </svg>
  );
}

/**
 * Input with error styling
 */
export function ValidatedInput({
  error,
  className = '',
  id,
  'aria-describedby': ariaDescribedBy,
  ...props
}) {
  const errorId = id ? `${id}-error` : undefined;
  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  
  return (
    <input
      {...props}
      id={id}
      className={`input ${error ? 'input-error' : ''} ${className}`}
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={describedBy}
    />
  );
}

/**
 * Textarea with error styling
 */
export function ValidatedTextarea({
  error,
  className = '',
  id,
  'aria-describedby': ariaDescribedBy,
  ...props
}) {
  const errorId = id ? `${id}-error` : undefined;
  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  
  return (
    <textarea
      {...props}
      id={id}
      className={`input ${error ? 'input-error' : ''} ${className}`}
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={describedBy}
    />
  );
}
